import { motion } from 'motion/react';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  Radar, Activity, ShieldCheck, Sparkles, ArrowRight, Database, GitBranch, FileSearch,
  Bug, Network, TrendingUp, Waves, Radio, Timer, Gavel, ScrollText, Workflow,
  KeyRound, Grid3x3, Brain, Lock, Cpu, Atom, History, ShieldAlert,
  Lightbulb, Layers, ShieldOff, CheckCircle2, XCircle, MinusCircle, Telescope, Zap,
  Compass, AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GlassCard } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { KpiCard } from '@/components/viz/KpiCard';
import { PageContainer } from '@/components/viz/PageHeader';
import { FloatingMark3D } from '@/components/viz/Chart3D';
import {
  getAuthUser,
  getDiscoveryCoverage,
  getSection9Report,
  getBreachReadiness,
  listDecisions,
} from '@/lib/api';
import { friendlyName } from '@/lib/display-name';

/**
 * Live KPI bento - replaces the previous hardcoded marketing numbers
 * (2,847 / 4 / 1,247 / 42ms with fake "+12% vs 30d" deltas) with real
 * per-tenant data fetched on mount. No fake trends, no fake deltas.
 *
 * Mirrors the proven pattern from DiscoverySection9 (useEffect + Promise
 * .allSettled + AbortController) which is the working reference for the
 * same endpoints. The earlier TanStack Query version had the Section 9
 * card stuck at 0 even though the same endpoint rendered 9 elsewhere -
 * this raw-fetch version sidesteps that.
 */
interface HomeKpis {
  piiAssets: number | null;
  section9Open: number | null;
  decisionsLastHour: number | null;
  recentBreaches90d: number | null;
  loading: boolean;
}

const INITIAL_KPIS: HomeKpis = {
  piiAssets: null,
  section9Open: null,
  decisionsLastHour: null,
  recentBreaches90d: null,
  loading: true,
};

// Module-scoped cache: even when the HomePage unmounts/remounts (which
// it does in dev/prod under StrictMode + tanstack-router transitions),
// the most-recent KPI snapshot is preserved here so the next mount can
// render real numbers immediately. The previous useEffect-only approach
// lost the response if it resolved post-unmount.
let LAST_KPIS: HomeKpis | null = null;
let INFLIGHT: Promise<HomeKpis> | null = null;

async function fetchHomeKpis(): Promise<HomeKpis> {
  if (INFLIGHT) return INFLIGHT;
  INFLIGHT = (async () => {
    const [coverageRes, section9Res, decisionsRes, breachRes] = await Promise.allSettled([
      getDiscoveryCoverage(),
      getSection9Report(),
      listDecisions({ limit: 500 }),
      getBreachReadiness(),
    ]);
    const piiAssets =
      coverageRes.status === 'fulfilled' ? coverageRes.value.classifiedAssets : null;
    const section9Open =
      section9Res.status === 'fulfilled' ? section9Res.value.openFindings : null;
    let decisionsLastHour: number | null = null;
    if (decisionsRes.status === 'fulfilled') {
      const cutoff = Date.now() - 60 * 60 * 1000;
      decisionsLastHour = decisionsRes.value.decisions.filter((d) => {
        const t = Date.parse(d.decidedAt);
        return Number.isFinite(t) && t >= cutoff;
      }).length;
    }
    const recentBreaches90d =
      breachRes.status === 'fulfilled' ? breachRes.value.recentBreaches ?? 0 : null;
    const snapshot: HomeKpis = {
      piiAssets,
      section9Open,
      decisionsLastHour,
      recentBreaches90d,
      loading: false,
    };
    LAST_KPIS = snapshot;
    INFLIGHT = null;
    return snapshot;
  })();
  return INFLIGHT;
}

function useHomeKpis(): HomeKpis {
  const [kpis, setKpis] = useState<HomeKpis>(LAST_KPIS ?? INITIAL_KPIS);

  useEffect(() => {
    let mounted = true;
    // Always fire a fresh fetch (cached snapshot is the optimistic
    // initial state). If unmount fires first, the second mount picks
    // up the module-level LAST_KPIS cache and renders real values
    // immediately while the second fetch refreshes them.
    fetchHomeKpis().then((snapshot) => {
      if (mounted) setKpis(snapshot);
    });
    return () => {
      mounted = false;
    };
  }, []);

  return kpis;
}

/** First name for a friendly greeting; falls back to a generic greeting. */
function firstNameFromUser(): string | null {
  const u = getAuthUser();
  if (!u) return null;
  // The bridge sets displayName = email (no name claim today), so a raw
  // split would render the whole address. friendlyName() turns
  // "arjun.rao@example.com" into "Arjun". See lib/display-name.
  return friendlyName(u.displayName, u.email).first;
}

export function HomePage() {
  const kpis = useHomeKpis();
  // OBS-3 (P1, 2026-05-26) - when an authenticated user lands on /,
  // show operator-voice copy ("Welcome back, X - here's what's live
  // on the platform today"). Unauthenticated visitors still see the
  // marketing pitch - they're the audience that copy was written for.
  const operatorFirstName = firstNameFromUser();
  const isAuthenticated = operatorFirstName !== null || getAuthUser() !== null;
  return (
    <PageContainer className="space-y-12 pb-20">
      {/* Hero - 3D mark + headline + CTAs */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center pt-4">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
          className="space-y-5"
        >
          <Badge variant="glass" className="gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse" />
            {isAuthenticated ? 'Live · India · Sovereign' : 'interop@1.1.0 · India · Sovereign'}
          </Badge>
          {isAuthenticated ? (
            <>
              <h1 className="text-4xl md:text-5xl font-display font-bold tracking-tight leading-[1.05]">
                {operatorFirstName ? `Welcome back, ${operatorFirstName}.` : 'Welcome back.'}
                <br />
                <span className="bg-gradient-to-r from-chart-1 via-chart-2 to-chart-7 bg-clip-text text-transparent">
                  Here's what's live on the platform today.
                </span>
              </h1>
              <p className="text-base text-muted-foreground max-w-xl leading-relaxed">
                Jump straight into Discovery to triage today's findings,
                open the Operator Dashboard for the live decision feed,
                or use the guided walkthrough if you want a refresher
                on the surface area.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-4xl md:text-5xl font-display font-bold tracking-tight leading-[1.05]">
                Privacy-deep data security
                <br />
                <span className="bg-gradient-to-r from-chart-1 via-chart-2 to-chart-7 bg-clip-text text-transparent">
                  for enterprises that can't leak.
                </span>
              </h1>
              <p className="text-base text-muted-foreground max-w-xl leading-relaxed">
                FOCTTA Discovery, Activity, and Detection - three modules that close the 263-day-to-6-hour
                detection gap RBI is penalising every Indian bank for. Built for DPDPA, deployable inside
                your VPC, with the only Consent-Aware Query Firewall in the Indian market.
              </p>
            </>
          )}
          {/* P1 OBS-1.alt (2026-05-26) - for unauthenticated visitors
              (evaluators, prospects), promote the guided tour as the
              primary CTA. Authenticated operators keep "Open Discovery"
              primary since they don't need the tour. */}
          <div className="flex flex-wrap gap-3">
            {!isAuthenticated && (
              <Button size="lg" asChild className="bg-gradient-to-r from-primary to-accent shadow-lg">
                <Link to="/" search={{ demo: 'tour' } as Record<string, unknown>}>
                  <Sparkles className="h-4 w-4" /> Start guided demo tour
                </Link>
              </Button>
            )}
            <Button size="lg" asChild variant={isAuthenticated ? 'default' : 'glass'}>
              <Link to="/dspm">
                Open Discovery <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button size="lg" variant="glass" asChild>
              <Link to="/detection">
                Operator Dashboard
              </Link>
            </Button>
            {isAuthenticated && (
              <Button size="lg" variant="glass" asChild className="border-primary/40 hover:border-primary">
                <Link to="/" search={{ demo: 'tour' } as Record<string, unknown>}>
                  <Sparkles className="h-4 w-4" /> Start guided demo tour
                </Link>
              </Button>
            )}
            {/* Phase 10 landing tour - operator-focused walkthrough of the
                admin + compliance surfaces. Aimed at first-time DPO / IT
                Admin on a new tenant. Separate storage key from the sales
                tour, so the two don’t clobber each other. */}
            <Button size="lg" variant="glass" asChild className="border-emerald-400/40 hover:border-emerald-300">
              <Link to="/" search={{ demo: 'ops' } as Record<string, unknown>}>
                <Compass className="h-4 w-4" /> Operator walkthrough
              </Link>
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <Badge variant="glass">DPDPA · Section 9</Badge>
            <Badge variant="glass">Aadhaar Vault</Badge>
            <Badge variant="glass">RBI 6-hour clock</Badge>
            <Badge variant="glass">Banking 2P Rule</Badge>
            <Badge variant="glass">Triple Redundancy</Badge>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.15, ease: [0.32, 0.72, 0, 1] }}
        >
          <FloatingMark3D height={420} />
        </motion.div>
      </section>

      {/* Live KPI bento - real per-tenant data (no fake trends/deltas).
          KpiCard's Motion-based renderer was rewritten 2026-05-26 to
          use React state + RAF (was relying on motion's subscriber
          tracking which decoupled across federated remotes), so this
          page now uses the shared KpiCard like every other dashboard. */}
      <section>
        <div className="flex items-end justify-between mb-3">
          <h2 className="text-xs uppercase tracking-wider text-muted-foreground">Live across all modules</h2>
          {kpis.loading && (
            <span className="text-[10px] text-muted-foreground/70">loading…</span>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard
            label="PII assets"
            value={kpis.piiAssets ?? 0}
            icon={Database}
            hint={!kpis.loading && kpis.piiAssets === null ? 'unavailable' : 'classified columns'}
          />
          <KpiCard
            label="Section 9 - open"
            value={kpis.section9Open ?? 0}
            icon={ShieldAlert}
            glow={(kpis.section9Open ?? 0) > 0 ? 'critical' : undefined}
            hint={!kpis.loading && kpis.section9Open === null ? 'unavailable' : 'minor-data findings'}
          />
          <KpiCard
            label="Decisions (last hour)"
            value={kpis.decisionsLastHour ?? 0}
            icon={Gavel}
            hint={!kpis.loading && kpis.decisionsLastHour === null ? 'unavailable' : 'DDR allow/mask/block'}
          />
          <KpiCard
            label="Recent breaches (90d)"
            value={kpis.recentBreaches90d ?? 0}
            icon={AlertTriangle}
            glow={(kpis.recentBreaches90d ?? 0) > 0 ? 'high' : undefined}
            hint={!kpis.loading && kpis.recentBreaches90d === null ? 'unavailable' : 'incident table'}
          />
        </div>
      </section>

      {/* Module quick-launch - bento grid */}
      <section>
        <h2 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Modules</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <ModuleTile
            to="/dspm"
            icon={Radar}
            label="Discovery"
            sub="DSPM"
            tagline="Find every PII column. Prove what is at rest."
            highlights={['Aadhaar Verhoeff', 'Section 9 detect', 'Subject data discovery', 'India moats']}
            from="from-chart-1/30"
            to_color="to-chart-2/10"
          />
          <ModuleTile
            to="/activity"
            icon={Activity}
            label="Activity"
            sub="DAM"
            tagline="See every query. Catch the leak before the breach."
            highlights={['Statement hashing', 'UEBA + Sonar', 'P-Wave detectors', 'RBI 6h clock']}
            from="from-chart-2/30"
            to_color="to-chart-7/10"
          />
          <ModuleTile
            to="/detection"
            icon={ShieldCheck}
            label="Detection"
            sub="DDR"
            tagline="Block the risky query before it runs."
            highlights={['Triple Redundancy', 'Banking 2P', 'Why-Blocked', '29 SIEM/SOAR']}
            from="from-chart-7/30"
            to_color="to-chart-1/10"
          />
          <ModuleTile
            to="/frontier"
            icon={Sparkles}
            label="Frontier"
            sub="Sovereign"
            tagline="Federated learning, post-quantum, sovereign-tier."
            highlights={['SecAgg+ DP', 'CRYSTALS-Kyber/Dilithium', 'Cilium Tetragon', 'BYO-LLM Tier 5']}
            from="from-chart-2/30"
            to_color="to-chart-7/10"
          />
        </div>
      </section>

      {/* Capability deep dives */}
      <section>
        <div className="flex items-end justify-between mb-3">
          <div>
            <h2 className="text-xs uppercase tracking-wider text-muted-foreground">Capability deep dives</h2>
            <p className="text-sm text-foreground mt-1">Detailed walkthroughs of the discovery and classification pipeline.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <DeepDiveTile
            to="/dspm/bootstrap"
            icon={Layers}
            title="Adaptive Asset Bootstrap"
            tagline="Eight evidence sources for asset discovery, ordered by confidence weight. Each level falls through to the next if unavailable."
            cta="View the cascade"
          />
          <DeepDiveTile
            to="/dspm/classifier"
            icon={Cpu}
            title="Multi-engine Classifier"
            tagline="Five independent engines (regex, NER, graph context, embeddings, optional LLM) vote per finding; aggregator handles partial outages."
            cta="View the engines"
          />
          <DeepDiveTile
            to="/dspm/no-db-access"
            icon={ShieldOff}
            title="No-Direct-DB-Access Mode"
            tagline="Six alternative evidence sources for environments where direct database scanner connection is not permitted."
            cta="View the sources"
          />
          <DeepDiveTile
            to="/innovations"
            icon={Lightbulb}
            title="Innovation Catalogue"
            tagline="Catalogue of cross-domain techniques applied to data security: aerospace fault tolerance, SPC, graph traversal, federated learning."
            cta="Open catalogue"
          />
        </div>
      </section>


      {/* Implemented capabilities catalogue */}
      <section>
        <div className="flex items-end justify-between mb-3">
          <div>
            <h2 className="text-xs uppercase tracking-wider text-muted-foreground">Implemented capabilities</h2>
            <p className="text-sm text-foreground mt-1">Reference list of features wired across the four modules.</p>
          </div>
          <Badge variant="glass">interop@1.1.0</Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {INNOVATIONS.map((inv, i) => (
            <motion.div
              key={inv.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: i * 0.025, ease: [0.32, 0.72, 0, 1] }}
            >
              <GlassCard className="p-4 h-full" lift>
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 shrink-0 rounded-md bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center text-primary">
                    <inv.icon className="h-4 w-4" />
                  </div>
                  <div className="space-y-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold leading-tight">{inv.label}</span>
                      <Badge variant="glass" className="text-[9px]">{inv.module}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground leading-snug">{inv.detail}</p>
                  </div>
                </div>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      </section>
    </PageContainer>
  );
}

interface ModuleTileProps {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  sub: string;
  tagline: string;
  highlights: string[];
  from: string;
  to_color: string;
}

function ModuleTile({ to, icon: Icon, label, sub, tagline, highlights, from, to_color }: ModuleTileProps) {
  return (
    <Link to={to} className="block h-full">
      <GlassCard shine className="p-5 h-full flex flex-col gap-4 group">
        <div className={`h-32 -m-5 mb-0 rounded-t-lg bg-gradient-to-br ${from} ${to_color} relative overflow-hidden flex items-center justify-center`}>
          <Icon className="h-14 w-14 text-foreground/80 group-hover:scale-110 transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]" />
          <div className="absolute inset-0 mesh-grid opacity-30" />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-baseline gap-2">
            <h3 className="text-lg font-display font-semibold">{label}</h3>
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{sub}</span>
          </div>
          <p className="text-xs text-muted-foreground leading-snug">{tagline}</p>
        </div>
        <div className="flex flex-wrap gap-1 pt-1">
          {highlights.map((h) => (
            <span key={h} className="text-[10px] px-1.5 py-0.5 rounded glass-lite text-muted-foreground">
              {h}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-1 text-xs text-primary mt-auto pt-2 group-hover:gap-2 transition-all">
          Open module <ArrowRight className="h-3 w-3" />
        </div>
      </GlassCard>
    </Link>
  );
}

// ─────────────────────────────────────────────────────────
// Featured Deep Dive tile (links to Innovations / Bootstrap / Classifier / NoDbAccess)
// ─────────────────────────────────────────────────────────
interface DeepDiveTileProps {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  tagline: string;
  cta: string;
}

function DeepDiveTile({ to, icon: Icon, title, tagline, cta }: DeepDiveTileProps) {
  return (
    <Link to={to} className="block h-full">
      <GlassCard shine lift className="p-5 h-full flex flex-col gap-3 group border-l-4 border-l-accent/60">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-gradient-to-br from-primary/20 to-accent/30 flex items-center justify-center text-accent">
            <Icon className="h-5 w-5" />
          </div>
          <Zap className="h-3.5 w-3.5 text-accent ml-auto" />
        </div>
        <h3 className="text-sm font-display font-semibold leading-tight">{title}</h3>
        <p className="text-[11px] text-muted-foreground leading-snug">{tagline}</p>
        <div className="flex items-center gap-1 text-xs text-primary mt-auto pt-1 group-hover:gap-2 transition-all">
          {cta} <ArrowRight className="h-3 w-3" />
        </div>
      </GlassCard>
    </Link>
  );
}

// Marketing/comparison content has been moved to docs/technical-brief/EXPERT-REVIEW-BRIEF.html
// to keep this operational app focused on capabilities, not positioning.

// _DEAD_CODE_BEGIN - remove on next refactor
type _DeadStatus = 'yes' | 'partial' | 'no' | 'unique';
interface _DeadRow {
  cap: string;
  detail: string;
  foctta: _DeadStatus;
  dspm: _DeadStatus;
  dam: _DeadStatus;
  siem: _DeadStatus;
  scripts: _DeadStatus;
}
const _DEAD_MOATS: _DeadRow[] = [
  {
    cap: '8-level adaptive asset discovery',
    detail: 'Cloud APIs → CMDB → EDR → SSO → HRIS → network → active enum → manual CSV. Discovery never goes blind.',
    foctta: 'unique', dspm: 'partial', dam: 'no', siem: 'partial', scripts: 'no',
  },
  {
    cap: '5-engine classifier voting',
    detail: 'Regex+checksum / DistilBERT-Indic NER / Column-graph sibling-walk / pgvector embeddings / Tier 5 BYO-LLM.',
    foctta: 'unique', dspm: 'no', dam: 'no', siem: 'no', scripts: 'no',
  },
  {
    cap: 'No-Direct-DB-Access mode',
    detail: '6 alternative evidence sources when a BFSI/PSU DBA refuses to grant scanner connection to production.',
    foctta: 'unique', dspm: 'no', dam: 'partial', siem: 'no', scripts: 'no',
  },
  {
    cap: 'Pre-execution query block (DDR)',
    detail: 'Block / mask / step-up the risky query BEFORE it runs. The engine sits in the data path, not after the fact.',
    foctta: 'yes', dspm: 'no', dam: 'no', siem: 'no', scripts: 'no',
  },
  {
    cap: 'Triple-Redundancy decision engine',
    detail: 'rule_engine + UEBA + policy_graph vote independently. 2/3 = step-up. 1/3 = degraded mode + alert.',
    foctta: 'unique', dspm: 'no', dam: 'no', siem: 'no', scripts: 'no',
  },
  {
    cap: 'Synthetic-Error Contract (BLK-XXX)',
    detail: 'One Request ID flows: SQL error → audit chain → SIEM ticket → user-visible Why-Blocked card.',
    foctta: 'unique', dspm: 'no', dam: 'no', siem: 'no', scripts: 'no',
  },
  {
    cap: 'DPDPA Section 9 minor-data flag',
    detail: 'India-specific minor-data classifier built in + auto-block at runtime, not just a tag in a report.',
    foctta: 'yes', dspm: 'partial', dam: 'no', siem: 'no', scripts: 'no',
  },
  {
    cap: 'Aadhaar Verhoeff / PAN MOD-36 / GSTIN',
    detail: 'India tax-ID structural validation native; not regex pattern-matching.',
    foctta: 'yes', dspm: 'partial', dam: 'no', siem: 'no', scripts: 'partial',
  },
  {
    cap: "Find a person's data (graph traversal)",
    detail: 'Hashed identifier in → all related findings out via column-graph traversal in seconds.',
    foctta: 'yes', dspm: 'partial', dam: 'no', siem: 'no', scripts: 'no',
  },
  {
    cap: 'Honey-PII canaries',
    detail: 'Decoy values seeded across data; any read fires an instant high-fidelity alert with zero false-positive rate.',
    foctta: 'unique', dspm: 'no', dam: 'partial', siem: 'partial', scripts: 'no',
  },
  {
    cap: 'Banking 2-Person rule (break-glass)',
    detail: 'Sensitive overrides require a second distinct approver - banking-grade dual control end-to-end.',
    foctta: 'yes', dspm: 'no', dam: 'no', siem: 'partial', scripts: 'no',
  },
  {
    cap: 'Customer-VPC deployment',
    detail: 'Data never leaves customer\'s network. Edge scanner pods + Decision Gateway run inside their cloud.',
    foctta: 'yes', dspm: 'partial', dam: 'partial', siem: 'partial', scripts: 'yes',
  },
  {
    cap: 'Federated learning (SecAgg+)',
    detail: 'Cross-tenant classifier improvement; coordinator never sees plaintext gradients.',
    foctta: 'unique', dspm: 'no', dam: 'no', siem: 'no', scripts: 'no',
  },
  {
    cap: 'Post-Quantum (Kyber + Dilithium)',
    detail: 'NIST Round-3 finalists across 5 algorithms via libOQS / CIRCL / AWS KMS for evidence-vault sealing.',
    foctta: 'yes', dspm: 'no', dam: 'no', siem: 'no', scripts: 'no',
  },
  {
    cap: 'BYO-LLM (9 providers, opt-in Tier 5)',
    detail: 'Off by default; opt-in per data-class. Anthropic / Bedrock / Vertex / Azure / OpenAI / Mistral / Cohere / vLLM.',
    foctta: 'unique', dspm: 'partial', dam: 'no', siem: 'partial', scripts: 'no',
  },
];

// _DEAD_CODE_END

const INNOVATIONS = [
  // Discovery
  { label: 'Aadhaar Verhoeff classifier', detail: '99% precision via UIDAI Verhoeff check + 12-digit pattern + India context.', module: 'Discovery', icon: Radar },
  { label: 'PAN MOD-36 + GSTIN check', detail: 'Indian tax-ID structural validation built in.', module: 'Discovery', icon: ShieldCheck },
  { label: 'Section 9 minor-data detect', detail: 'DPDPA Section 9 violations surfaced + auto-blocked at runtime.', module: 'Discovery', icon: ShieldAlert },
  { label: 'India moats classifier pack', detail: 'Aadhaar Vault, Account Aggregator, DigiLocker compliance signals.', module: 'Discovery', icon: FileSearch },
  { label: "Find a person's data (graph traversal)", detail: 'Hashed identifier in → all related findings out via column-graph.', module: 'Discovery', icon: GitBranch },
  { label: 'Honey-PII Canaries', detail: 'Decoy values seeded across data; any read fires an instant alert.', module: 'Discovery', icon: Bug },
  // Activity
  { label: 'Statement hashing', detail: 'Audit trail never becomes a PII liability - only SHA-256 hashes stored.', module: 'Activity', icon: ScrollText },
  { label: 'UEBA Western Electric Rules', detail: 'Per-actor behavioral baselines with 4-rule statistical control.', module: 'Activity', icon: TrendingUp },
  { label: 'Submarine Sonar', detail: 'KL-divergence on application acoustic signatures detects new app behavior.', module: 'Activity', icon: Waves },
  { label: 'Earthquake Early Warning', detail: '6 P-wave detectors fire seconds before a breach pattern completes.', module: 'Activity', icon: Radio },
  { label: 'Mass-Export Pre-Cog', detail: 'Block 5×/10×/50× baseline export attempts before completion.', module: 'Activity', icon: Network },
  { label: 'Breach Readiness 6h clock', detail: 'RBI 6-hour clock + DPDPA 72h + GDPR 72h + HIPAA 60d countdowns.', module: 'Activity', icon: Timer },
  // Detection
  { label: 'Triple Redundancy decisions', detail: 'rule_engine + UEBA + policy_graph vote; 2/3 = step-up; 1/3 = alert (degraded).', module: 'Detection', icon: ShieldCheck },
  { label: 'Synthetic-Error Contract', detail: 'BLK-XXXXXXXXXXXX Request ID flows: SQL error → audit → SIEM → Why-Blocked.', module: 'Detection', icon: Gavel },
  { label: 'Banking Two-Person Rule', detail: 'Break-glass + critical playbooks require a second distinct approver.', module: 'Detection', icon: KeyRound },
  { label: 'Pre-execution prevention', detail: 'Block/mask/step-up the query BEFORE it runs (not after).', module: 'Detection', icon: ShieldAlert },
  { label: 'Forest Fire containment', detail: 'Quarantine adjacent assets when one is compromised.', module: 'Detection', icon: Workflow },
  { label: 'Predict-before-publish', detail: 'Dry-run rule changes against last 30d events before going live.', module: 'Detection', icon: ScrollText },
  { label: '29-vendor SIEM/SOAR registry', detail: 'IBM QRadar / Splunk / Sentinel / Cortex XSOAR - name it, we have it.', module: 'Detection', icon: Grid3x3 },
  // Frontier
  { label: 'Federated learning (SecAgg+)', detail: 'Cross-tenant classifier improvement; coordinator never sees plaintext.', module: 'Frontier', icon: Brain },
  { label: 'SMPC benchmarks (Mersenne-61)', detail: '10-benchmark catalogue; aggregate visible, per-tenant inputs never.', module: 'Frontier', icon: Network },
  { label: 'Post-Quantum (Kyber + Dilithium)', detail: 'NIST Round-3 finalists across 5 algorithms via libOQS / CIRCL / AWS KMS.', module: 'Frontier', icon: Lock },
  { label: 'eBPF Kernel Sensor (Mode D)', detail: 'Cilium Tetragon, 5 tracing policies, kernel ≥5.8 + SPIFFE attestation.', module: 'Frontier', icon: Cpu },
  { label: 'Tier 5 BYO-LLM (9 providers)', detail: 'Anthropic / Bedrock / Vertex / Azure OpenAI / vLLM / Mistral / Cohere.', module: 'Frontier', icon: Atom },
  { label: 'Compliance Replay (15 regulators)', detail: 'Counter-factual: "what if RBI 6h applied 90 days earlier?"', module: 'Frontier', icon: History },
  // Cross-cutting
  { label: 'Hash-chained egress log', detail: 'Tamper-evident proof every byte of metadata is auditable.', module: 'Foundation', icon: GitBranch },
  { label: 'Confidential VM attestation', detail: 'AWS Nitro / Azure DCsv3 / AMD SEV-SNP - even cloud admins can\'t read.', module: 'Foundation', icon: ShieldCheck },
  { label: 'Customer-VPC deployment', detail: 'BFSI/PSU-grade: data never leaves customer\'s network.', module: 'Foundation', icon: Network },
  { label: 'Single-SPA license-driven UI', detail: 'Same dashboard, modules light up by license bit. No redeploy.', module: 'Foundation', icon: Sparkles },
];
